import React, { useState } from "react";
import { Box, Button, Stack, Typography, TextField, InputAdornment, Switch } from "@mui/material";
import PriceLabelSvg from "@/assets/svg/PriceLabelSvg";
import { useTheme } from "@mui/material/styles";
import ArrowPaidButton from "@/assets/svg/ArrowPaidButton";
import { stylesPaidContentLine } from "./stylesPaidContentLine";

import { formatWithCommas } from "@/helpers/formatWithCommas";

interface PaidContentLineProps {
  lineRef: React.RefObject<HTMLDivElement | null>;
  handleMouseDown: (e: React.MouseEvent) => void;
  togglePaidContent: boolean;
  setTogglePaidContent: (val: boolean) => void;
  allowAnyAmount: boolean;
  setAllowAnyAmount: (val: boolean) => void;
  price: string;
  setPrice: (val: string) => void;
  lineY: number;
  setInvalidPrice: any;
  isEditing?: boolean;
}
const MAX_PRICE_DIGITS = 11;
const MIN_PRICE = 0.25;
const PaidContentLine: React.FC<PaidContentLineProps> = ({
  lineRef,
  handleMouseDown,
  togglePaidContent,
  setTogglePaidContent,
  allowAnyAmount,
  setAllowAnyAmount,
  price,
  setPrice,
  lineY,
  setInvalidPrice,
}) => {
  const theme = useTheme();
  const [priceApplied, setPriceApplied] = useState(() => {
    const num = Number(price);
    return !!price && Number.isFinite(num) && num >= MIN_PRICE;
  });

  const isValidPrice = (value: string) => {
    if (!value || value === "." || value === "0.") return false;

    const num = Number(value);
    return Number.isFinite(num) && num >= MIN_PRICE;
  };

  const normalizePrice = (value: string) => {
    let normalized = value.replace(/[^0-9.]/g, "");

    const parts = normalized.split(".");
    if (parts.length > 2) {
      normalized = parts[0] + "." + parts.slice(1).join("");
    }

    return normalized.slice(0, MAX_PRICE_DIGITS);
  };

  return (
    <>
      <Box
        ref={lineRef}
        onMouseDown={handleMouseDown}
        sx={{
          ...stylesPaidContentLine.container,
          top: lineY,
          position: "absolute",
          cursor: "ns-resize",
        }}
      >
        <Box sx={stylesPaidContentLine.line(togglePaidContent, theme)} />

        <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} zIndex={100}>
          <Button
            sx={stylesPaidContentLine.mainButton(togglePaidContent, theme)}
            onClick={() => setTogglePaidContent(!togglePaidContent)}
          >
            Paid content below this line
            <Box sx={stylesPaidContentLine.buttonInnerBox}>
              <Typography sx={stylesPaidContentLine.setPriceText(togglePaidContent, theme)}>
                {priceApplied ? "Edit" : "Set"} price
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  transform: togglePaidContent ? "rotate(-90deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              >
                <ArrowPaidButton
                  color={
                    togglePaidContent
                      ? theme.palette.custom.mainWhite
                      : theme.palette.custom.mainBlue
                  }
                />
              </Box>
            </Box>
          </Button>
        </Stack>

        {togglePaidContent && (
          <Stack
            bgcolor={"#F7F7F77D"}
            position="relative"
            top={"-40px"}
            direction={"column"}
            alignItems={"center"}
            width={"100%"}
            sx={stylesPaidContentLine.expandedContainer(theme)}
          >
            <Box sx={stylesPaidContentLine.containerTitle}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                  height: "106px",
                  gap: "12px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    height: "70px",
                    gap: "4px",
                  }}
                >
                  <PriceLabelSvg style={stylesPaidContentLine.priceLabelSvg} />
                  <Typography sx={stylesPaidContentLine.title}>Set a price to continue</Typography>
                </Box>
                <Stack sx={stylesPaidContentLine.containerSwich}>
                  <Typography sx={stylesPaidContentLine.labelText}>
                    Allow customers to pay they want
                  </Typography>
                  <Switch
                    sx={stylesPaidContentLine.customSwitch}
                    checked={allowAnyAmount}
                    onChange={() => setAllowAnyAmount(!allowAnyAmount)}
                  />
                </Stack>
              </Box>

              <Box
                sx={{
                  ...stylesPaidContentLine.inputWrapper,
                  margin: allowAnyAmount ? "30px auto 20px auto" : "64px auto 20px auto",
                }}
              >
                {allowAnyAmount && (
                  <Typography sx={stylesPaidContentLine.sectionHintTextStyles}>
                    Specify the minimum amount you want to be paid (Optional)
                  </Typography>
                )}
                <TextField
                  variant="outlined"
                  placeholder=""
                  value={formatWithCommas(price)}
                  onChange={(e) => {
                    const normalized = normalizePrice(e.target.value);

                    setPrice(normalized);
                    setInvalidPrice(!isValidPrice(normalized));
                  }}
                  sx={{
                    ...stylesPaidContentLine.mainInput,
                    "& .MuiOutlinedInput-root": {
                      minWidth: "286px",
                      width: "286px",
                      height: "48px",
                      borderRadius: "30px",
                      paddingRight: 0,
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #4285f4",
                      outline: "none",
                      "&:hover": { border: "1px solid #4285f4" },
                      "&.Mui-focused": { border: "1px solid #4285f4", outline: "none" },
                      "& fieldset": { border: "none !important" },
                      "&:hover fieldset": { border: "none !important" },
                      "&.Mui-focused fieldset": { border: "none !important" },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            position: "absolute",
                            top: 16.5,
                            left: 30 + price.length * 10,
                            fontFamily: "Inter",
                            fontWeight: 500,
                            fontStyle: "Medium",
                            fontSize: "12px",
                            lineHeight: "16px",
                            letterSpacing: "0%",
                            textAlign: "center",
                            verticalAlign: "middle",
                            color: "#8BC5F4",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {/* {price ? `$${formatWithCommas(Number(price).toFixed(2))}` : ""} */}
                        </Typography>

                        <Button
                          onClick={() => {
                            setTogglePaidContent(false);
                            setPriceApplied(true);
                          }}
                          variant="contained"
                          sx={stylesPaidContentLine.applyButton}
                          disabled={!isValidPrice(price)}
                        >
                          Apply
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography
                  sx={{
                    ...stylesPaidContentLine.helperTextStyles,
                    color:
                      price && !isValidPrice(price)
                        ? theme.palette.error.main
                        : stylesPaidContentLine.helperTextStyles.color,
                  }}
                >
                  {price && !isValidPrice(price)
                    ? `Minimum price is ${MIN_PRICE} USDC`
                    : "Please use USDC when entering the price."}
                </Typography>
              </Box>
            </Box>
          </Stack>
        )}
      </Box>

      <div style={stylesPaidContentLine.blurOverlay(lineY)} />
    </>
  );
};

export default PaidContentLine;
